import { useState, useCallback, useEffect, useRef } from 'react';
import { Notification } from '../types';
import { EMPLOYEES_SYNC_INTERVAL_MS } from './appDefaults';
import {
  broadcastCompanyNotifications,
  clearCompanyNotifications,
  dismissCompanyNotification,
  fetchCompanyNotifications,
} from '../services/notificationApi';
import type { CompanyNotificationPayload } from '../utils/employeeNotificationMessages';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export interface UseNotificationsParams {
  getVerifiedDate: () => Date;
  isAuthenticated: boolean;
  currentCompanyId: string | null;
  isAdmin: boolean;
  postgresApiUrl: string;
}

/**
 * Notificações do board — servidor (fan-out para não-admins) + locais (erros da sessão).
 * @param params - Sessão, empresa e URL da API
 * @returns Estado e ações de notificações
 */
export function useNotifications({
  getVerifiedDate,
  isAuthenticated,
  currentCompanyId,
  isAdmin,
  postgresApiUrl,
}: UseNotificationsParams) {
  const [serverNotifications, setServerNotifications] = useState<Notification[]>([]);
  const [localNotifications, setLocalNotifications] = useState<Notification[]>([]);
  const refreshRef = useRef<() => Promise<void>>(async () => {});

  const notifications = [...serverNotifications, ...localNotifications].sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
  );

  const refreshServerNotifications = useCallback(async () => {
    if (!isAuthenticated || !currentCompanyId || isAdmin) {
      setServerNotifications([]);
      return;
    }
    const { data, error } = await fetchCompanyNotifications(postgresApiUrl, currentCompanyId);
    if (!error) {
      setServerNotifications(data);
    }
  }, [isAuthenticated, currentCompanyId, isAdmin, postgresApiUrl]);

  useEffect(() => {
    refreshRef.current = refreshServerNotifications;
  }, [refreshServerNotifications]);

  useEffect(() => {
    if (!isAuthenticated || !currentCompanyId || isAdmin) {
      setServerNotifications([]);
      return;
    }

    void refreshServerNotifications();

    const intervalId = window.setInterval(() => {
      void refreshRef.current();
    }, EMPLOYEES_SYNC_INTERVAL_MS);

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        void refreshRef.current();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [isAuthenticated, currentCompanyId, isAdmin, refreshServerNotifications]);

  const addNotification = useCallback(
    (title: string, message: string, type: 'add' | 'delete' | 'info') => {
      const newNotif: Notification = {
        id: `local-${Date.now()}`,
        title,
        message,
        type,
        timestamp: getVerifiedDate().toISOString(),
        read: false,
        source: 'local',
      };
      setLocalNotifications((prev) => [newNotif, ...prev].slice(0, 50));
    },
    [getVerifiedDate],
  );

  const broadcastNotifications = useCallback(
    async (payloads: CompanyNotificationPayload[]) => {
      if (!currentCompanyId || !payloads.length) return;
      const error = await broadcastCompanyNotifications(postgresApiUrl, currentCompanyId, payloads);
      if (error) {
        console.warn('[notifications] Falha ao propagar:', error.message);
        return;
      }
      if (!isAdmin) {
        await refreshServerNotifications();
      }
    },
    [currentCompanyId, postgresApiUrl, isAdmin, refreshServerNotifications],
  );

  const dismissNotification = useCallback(
    async (notificationId: string) => {
      const isServer = UUID_RE.test(notificationId);
      if (isServer && currentCompanyId) {
        const error = await dismissCompanyNotification(
          postgresApiUrl,
          currentCompanyId,
          notificationId,
        );
        if (error) {
          console.warn('[notifications] Falha ao remover:', error.message);
          return;
        }
        setServerNotifications((prev) => prev.filter((n) => n.id !== notificationId));
        return;
      }
      setLocalNotifications((prev) => prev.filter((n) => n.id !== notificationId));
    },
    [currentCompanyId, postgresApiUrl],
  );

  const clearNotifications = useCallback(async () => {
    if (currentCompanyId && !isAdmin) {
      const error = await clearCompanyNotifications(postgresApiUrl, currentCompanyId);
      if (error) {
        console.warn('[notifications] Falha ao limpar:', error.message);
        return;
      }
    }
    setServerNotifications([]);
    setLocalNotifications([]);
  }, [currentCompanyId, isAdmin, postgresApiUrl]);

  const resetNotifications = useCallback(() => {
    setServerNotifications([]);
    setLocalNotifications([]);
  }, []);

  return {
    notifications,
    addNotification,
    broadcastNotifications,
    dismissNotification,
    clearNotifications,
    resetNotifications,
    refreshServerNotifications,
    setNotifications: setLocalNotifications,
  };
}
